import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { ComponentBase, RentalInfo, BookingErrorCode, RentalStatus, ResultDetailLevel } from 't4core';
import * as moment from 'moment';
import { TrailerInfoComponent } from '../../../Rental/dialogs/trailer-info/trailer-info.component';
import { AppSettingsService, AppIntegrationService } from 't4-app-integration';
import { BookingService } from 'app-components';

interface Event {
  navigate: string,
  booking: RentalInfo,
  event?: string
}
enum OnewayEstimateStatus {
  Confirmed = 0,
  TrailerOnSite = 1,
  NoTrailer = 2,
}
@Component({
    selector: 'app-booking-overview',
    templateUrl: './booking-overview.component.html',
    standalone: false
})
export class BookingOverviewComponent extends ComponentBase implements OnInit {
  @Input() public booking: RentalInfo;
  @Output() cancelled: EventEmitter<number> = new EventEmitter();
  @Output() navigate: EventEmitter<Event> = new EventEmitter();
  public showMenu: boolean = false;
  public status = RentalStatus;
  public countdown: moment.Duration;
  public late: boolean;
  public bookingQueue: RentalInfo;
  public confirmationAmbitionDate: Date;

  public onewayStatus: OnewayEstimateStatus;
  public onewayIsTrailerOnStation: boolean = false;

  public get canChangeBooking(): boolean {
    if (!this.booking?.PickupTime) {
      return true;
    }

    const pickupTime = moment.isMoment(this.booking.PickupTime)
      ? this.booking.PickupTime
      : moment(this.booking.PickupTime);

    return pickupTime.isAfter(moment());
  }

  constructor(public appSettings: AppSettingsService, private appService: AppIntegrationService, private bookingService: BookingService) {
    super(); 
  }

  async ngOnInit() {
    if (this.booking) {
      this.confirmationAmbitionDate = new Date(this.booking.PickupTime.toDate().getTime() - (5 * 24 * 60 * 60 * 1000));
    }

    if (this.booking.IsOneWay && this.booking.Status <= 50) {
      this.onewayStatus = OnewayEstimateStatus.NoTrailer;

      this.bookingQueue = await this.Api.get<RentalInfo>("Oneway/GetEstimatedDate", { rentalId: this.booking.Id }, null, null, ResultDetailLevel.Basic);
      if (this.bookingQueue) {
        if (this.bookingQueue.BindsAt) {
          this.onewayStatus = OnewayEstimateStatus.Confirmed;
        }
      }

      //If its Confirmed, we check if the trailer is on the station, or is incomming.
      if (this.booking.Status == 50) {
        this.onewayIsTrailerOnStation = await this.Api.get<boolean>("OneWay/IsRentalObjectOnStation", { rentalId: this.booking.Id })
      }

    }


  }


  secondTick() {
    if (this.booking) {
      this.countdown = moment.duration(this.booking.ReturnTime.diff(moment()));
    }
  }

  public async cancel() {

    if (!this.canChangeBooking) {
      this.UI.alert("Intellitrailer.MyPage.BookingOverview.CancelBookingFailedChangesNotAllowed.Title", "Can’t cancel booking.", "Intellitrailer.MyPage.BookingOverview.CancelBookingFailedChangesNotAllowed.Message", "This booking can’t be cancelled because its start time has passed or changes are no longer allowed at this state.");
      return;
    }


    let isUserCancelConfirmed = await this.UI.prompt("Intellitrailer.MyPage.BookingOverview.CancellationFeedback.Headline", "Cancellation Feedback", "Intellitrailer.MyPage.BookingOverview.ConfirmBookingCancellation.CommentInfo", "Please let us know the reason for your cancellation. Your feedback helps us do better.");
    if (isUserCancelConfirmed == null) return;

    let token = this.UI.beginLoading("Intellitrailer.MyPaage.Overview.CancelBookingMsg", "Canceling booking", null);
    let result = await this.Api.delete<BookingErrorCode>("/Rental/Cancel",
      {
        booking: this.booking.Id,
        comment: isUserCancelConfirmed
      });
    this.UI.loaderCompleted(token);
    if (result == BookingErrorCode.Valid) {
      this.bookingService.clearChangeBookingFlow();
      this.appService.refreshRentals("Cancellation", this.booking.Id);
      this.UI.alert("Intellitrailer.MyPage.BookingOverview.CancelBookingSuccess.Title", "Cancelled!", "Intellitrailer.MyPage.BookingOverview.CancelBookingSuccess.Message", "The booking has been cancelled. Any paid amount will be refunded shortly.");
      this.cancelled.emit(this.booking.Id);   
    }
    else if (result = BookingErrorCode.ChangesNotAllowed) {
      this.UI.alert("Intellitrailer.MyPage.BookingOverview.CancelBookingFailedChangesNotAllowed.Title", "Can’t cancel booking.", "Intellitrailer.MyPage.BookingOverview.CancelBookingFailedChangesNotAllowed.Message", "This booking can’t be cancelled because its start time has passed or changes are no longer allowed at this state.");
      return false;
    }
    else {
      this.UI.alert("Turbo.Core.BookingLink.resendEmailFailed.Headline", "Failed!", "Intellitrailer.MyPage.BookingOverview.CancelBookingFailed.Message", "The booking could not be cancelled. Please try again.");
      this.cancelled.emit(null);
    }
  }


  public async change() {
    if (!this.canChangeBooking) {
      this.UI.alert("Intellitrailer.MyPage.BookingOverview.ChangeNotAllowed.Title", "Cannot Change Booking", "Intellitrailer.MyPage.BookingOverview.ChangeNotAllowed.Message", "Since the start time has passed, you can no longer change the booking.");
      return;
    }

    var emitInfo: Event = { navigate: "ChangeBooking", booking: this.booking };
    this.navigate.emit(emitInfo);
  }

  public async openBooking() {
    var emitInfo: Event = { navigate: "", booking: this.booking };
    if (this.booking.Status > this.status.ReservedAndBound && this.booking.Status < this.status.Returned) {
      emitInfo.navigate = "Rental.InFlight";
      this.navigate.emit(emitInfo);
    }
    else {
      emitInfo.navigate = "Rental.BookingInfo";
      this.navigate.emit(emitInfo);
    }
  }

  public async createFaultReport() {
    var emitInfo: Event = { navigate: "FaultReport", booking: this.booking, event: "FaultReport" };
    this.navigate.emit(emitInfo);
  }

  public sendEmail() {
    this.Api.get("Rental/resendEmailConfirmation", { rentalId: this.booking.Id });
  }

  public openTrailerInfo() {
    const dialogRef = this.Dialog.open(TrailerInfoComponent, {
      width: '350px',
      data: this.booking.ObjectId
    });

    dialogRef.afterClosed().subscribe(async result => {
    });
  }
}
